import type { TFunction } from 'i18next'
import { matchPath } from 'react-router'

import {
  FaBuildingRegular,
  FaCalculatorRegular,
  FaCalendarCheckRegular,
  FaChartLineRegular,
  FaDatabaseRegular,
  FaFileChartColumnRegular,
  FaRightLeftRegular,
  FaSitemapRegular,
  FaSlidersRegular,
  FaSunBrightRegular,
} from '@enechain/ecloud-icons'

import type { SubMenuItem } from '~/components/layouts/MainLayout/SubMenuItems'
import { type ReleaseGate, type ReleaseGateState } from '~/hooks/releaseGate'
import { type SideMenuDot, resolveGroupSideMenuDot } from '~/models/sideMenuDot'
import { routePaths } from '~/routes/routePaths'

/**
 * link 項目が出る条件に使う gate。 値は `filterMenuGroups` に渡す状態の key。
 * `workflowAutomation` は flag `ebalance-workflow-automation`、 `splitSupplyPlan` は flag
 * `ebalance-split-supply-plan` (どちらも on 以外 = pending / off では出さない)。
 */
export type MenuGate = ReleaseGate

export type MenuGateState = ReleaseGateState

export type MenuGateStates = Readonly<Record<MenuGate, MenuGateState>>

export type MenuGroupItem =
  | { kind?: 'link'; path: string; nameKey: string; gate?: MenuGate }
  | { kind: 'section'; labelKey: string }
  | { kind: 'divider' }

export type MenuGroup = {
  icon: typeof FaCalculatorRegular
  labelKey: string
  prefix: string
  // design のトップレベル セクション境界 (= 操作頻度でのグルーピング)。 true の group の
  // 直前に区切り線を 1 本引く。 design/js/include-nav.js の `{ type: 'navheader' }`
  // (日次オペレーション / モニタリング・分析 / 管理・設定) が `<hr class="nav-divider">`
  // として描画される形に対応する (見出しの文字は design 側で非表示 = 線だけ残す)。
  // 先頭 group (= 業務ワークフロー) は上端なので線を引かない (design も上端は線なし)。
  sectionDividerAbove?: boolean
  /**
   * 下層項目を持たない **単一リンク** の group (= 副パネルも chevron も出さず、
   * クリックで `items` の唯一の link へ直接遷移する)。
   *
   * `docs/specs/common/shared-components.md` §1 表 8 行目 (システム管理) が該当する
   * (2026-10-01 PdM 判断 #9228: 個別画面への導線は システム管理トップのカードが担う)。
   * 同 spec の AC-SC-003 / AC-SC-029 は「グループ」 向けの AC なので、 この group は対象外。
   *
   * active 判定は items の一致ではなく `prefix` 配下で行う (配下の画面はメニューに
   * 載っていないため、 items 一致では配下にいる間 current が外れてしまう)。
   */
  singleLink?: boolean
  items: MenuGroupItem[]
}

export const menuGroups: MenuGroup[] = [
  // 0. 業務ワークフロー (オペレーション起点。 ケースを作成し需要想定→供給力→入札→計画提出のフローで進める)
  {
    icon: FaSitemapRegular,
    labelKey: 'common:nav.workflow',
    prefix: '/workflow',
    items: [
      {
        path: routePaths.workflowCases.path,
        nameKey: routePaths.workflowCases.name,
      },
      { kind: 'divider' },
      {
        path: routePaths.adminWorkflowTemplates.path,
        nameKey: routePaths.adminWorkflowTemplates.name,
      },
      // 自動実行履歴 (auto-execution-history.md AC-AEH-005)。 flag OFF・評価前は出さない
      {
        path: routePaths.workflowAutoExecutionHistory.path,
        nameKey: routePaths.workflowAutoExecutionHistory.name,
        gate: 'workflowAutomation',
      },
    ],
  },
  // 1. 需要想定
  {
    icon: FaFileChartColumnRegular,
    labelKey: 'common:nav.demand',
    prefix: '/demand',
    items: [
      {
        path: routePaths.demandForecastList.path,
        nameKey: routePaths.demandForecastList.name,
      },
      { kind: 'divider' },
      {
        path: routePaths.demandSingularDay.path,
        nameKey: routePaths.demandSingularDay.name,
      },
      // 需要想定設定 (design include-nav.js の juyoItems と同じく特異日カレンダーの後)。
      // 需要グループはこの画面のタブ。未公開の組織にも項目は出し、開くと PlaceholderPage になる
      {
        path: routePaths.demandSettings.path,
        nameKey: routePaths.demandSettings.name,
      },
    ],
  },
  // 2. 供給力
  {
    icon: FaSunBrightRegular,
    labelKey: 'common:nav.supply',
    prefix: '/supply',
    items: [
      { kind: 'section', labelKey: 'common:nav.section.supplyPlan' },
      {
        path: routePaths.supplyBilateral.path,
        nameKey: routePaths.supplyBilateral.name,
      },
      // 他メニューはP1対象外だから非表示（取引契約管理・48点データ・需給ギャップのみ表示）
      // {
      //   path: routePaths.adminNotificationChange.path,
      //   nameKey: routePaths.adminNotificationChange.name,
      // },
      // {
      //   path: routePaths.supplyPlant.path,
      //   nameKey: routePaths.supplyPlant.name,
      // },
      {
        path: routePaths.supply48Data.path,
        nameKey: routePaths.supply48Data.name,
      },
      // 他メニューはP1対象外だから非表示（取引契約管理・48点データ・需給ギャップのみ表示）
      // {
      //   path: routePaths.adminIndirectTransmission.path,
      //   nameKey: routePaths.adminIndirectTransmission.name,
      // },
      // {
      //   path: routePaths.supplyRenewForecast.path,
      //   nameKey: routePaths.supplyRenewForecast.name,
      // },
      { path: routePaths.supplyGap.path, nameKey: routePaths.supplyGap.name },
      // 他メニューはP1対象外だから非表示（取引契約管理・48点データ・需給ギャップのみ表示）
      // { kind: 'section', labelKey: 'common:nav.section.supplyOther' },
      // {
      //   path: routePaths.adminFipPremium.path,
      //   nameKey: routePaths.adminFipPremium.name,
      // },
    ],
  },
  // 3. 市場取引 (旧「入札」。 2026-10-05 PdM 判断で改称、 #7526 / spec PR #9673。
  //    i18n キー名は nav.bidding のまま)
  {
    icon: FaRightLeftRegular,
    labelKey: 'common:nav.bidding',
    prefix: '/bidding',
    items: [
      // [8080 準拠] 先頭の section 見出しは置かない (8080 `js/include-nav.js` の市場取引グループ。
      // グループ見出しと重複するため撤去済み)
      {
        path: routePaths.biddingSpot.path,
        nameKey: routePaths.biddingSpot.name,
      },
      {
        path: routePaths.biddingIntraday.path,
        nameKey: routePaths.biddingIntraday.name,
      },
      // 設定・参照 セグメント (`docs/specs/common/shared-components.md` §1
      // メニューグループ構成「3 | 入札 | 設定・参照 | 入札制限 / 承認フロー / スポット価格 / 価格予測」)。
      // 入札制限は URL が `/admin/` 配下だが、 group 所属は menu item 一致で引くため
      // ここに置けば 入札 group が current になる。
      { kind: 'divider' },
      {
        path: routePaths.adminBidLimits.path,
        nameKey: routePaths.adminBidLimits.name,
      },
      {
        path: routePaths.biddingSpotPrice.path,
        nameKey: routePaths.biddingSpotPrice.name,
      },
      // 清算セグメント (shared-components.md §1 メニューグループ構成。 スポット価格・
      // 価格予測の下に、 区切り線で分けて置く)。
      { kind: 'divider' },
      {
        path: routePaths.biddingSettlement.path,
        nameKey: routePaths.biddingSettlement.name,
      },
    ],
  },
  // 4. 計画提出
  {
    icon: FaCalendarCheckRegular,
    labelKey: 'common:nav.plan',
    prefix: '/plan',
    items: [
      {
        // submission-management-dashboard-skeleton Decision 17: 「提出管理」の
        // 遷移先を `planSubmission` (`/plan/submission`、互換用 PlaceholderPage)
        // から `submissionManagement` (`/submission-management`) へ付け替える。
        // flag OFF の組織も同じ RPC 経由でプレースホルダに落ちる (Decision 9)。
        path: routePaths.submissionManagement.path,
        nameKey: routePaths.submissionManagement.name,
      },
      {
        path: routePaths.planProcurement.path,
        nameKey: routePaths.planProcurement.name,
      },
      {
        path: routePaths.planGenSales.path,
        nameKey: routePaths.planGenSales.name,
      },
      {
        path: routePaths.planDemandSuppression.path,
        nameKey: routePaths.planDemandSuppression.name,
      },
      {
        path: routePaths.planSplitSupply.path,
        nameKey: routePaths.planSplitSupply.name,
        gate: 'splitSupplyPlan',
      },
    ],
  },
  // 5. 同時同量
  {
    icon: FaCalculatorRegular,
    labelKey: 'common:nav.balance',
    prefix: '/balance',
    items: [
      {
        path: routePaths.balanceDemand.path,
        nameKey: routePaths.balanceDemand.name,
      },
      {
        path: routePaths.balanceGeneration.path,
        nameKey: routePaths.balanceGeneration.name,
      },
      // design の deployed nav (design/js/include-nav.js) は 同時同量 グループ末尾に
      // divider + 欠損補完設定 を置く。divider は現行 MenuGroup 型に無いので
      // link のみを同位置に並べる。
      {
        path: routePaths.balanceImputationPolicy.path,
        nameKey: routePaths.balanceImputationPolicy.name,
      },
    ],
  },
  // 6. 収益管理 (design の「モニタリング・分析」 セクション先頭 = 直前に区切り線)
  {
    icon: FaChartLineRegular,
    labelKey: 'common:nav.revenue',
    prefix: '/revenue',
    sectionDividerAbove: true,
    items: [
      {
        path: routePaths.revenueForecast.path,
        nameKey: routePaths.revenueForecast.name,
      },
      {
        path: routePaths.revenueInvoice.path,
        nameKey: routePaths.revenueInvoice.name,
      },
      {
        path: routePaths.adminCapacityContribution.path,
        nameKey: routePaths.adminCapacityContribution.name,
      },
    ],
  },
  // 7. 需給情報
  {
    icon: FaDatabaseRegular,
    labelKey: 'common:nav.jukyu',
    prefix: '/jukyu',
    items: [
      { kind: 'section', labelKey: 'common:nav.section.jukyuBalance' },
      {
        path: routePaths.jukyuAreaBalance.path,
        nameKey: routePaths.jukyuAreaBalance.name,
      },
      {
        path: routePaths.jukyuReserveRate.path,
        nameKey: routePaths.jukyuReserveRate.name,
      },
      {
        path: routePaths.jukyuInterconnection.path,
        nameKey: routePaths.jukyuInterconnection.name,
      },
      {
        path: routePaths.biddingImbalancePrice.path,
        nameKey: routePaths.biddingImbalancePrice.name,
      },
      {
        path: routePaths.jukyuHjks.path,
        nameKey: routePaths.jukyuHjks.name,
      },
      { kind: 'section', labelKey: 'common:nav.section.jukyuWeather' },
      {
        path: routePaths.weatherForecast.path,
        nameKey: routePaths.weatherForecast.name,
      },
    ],
  },
  // 8. 顧客管理
  {
    icon: FaBuildingRegular,
    labelKey: 'common:nav.customer',
    prefix: '/customer',
    items: [
      // タブ (需要家一覧 / 契約者 / 契約推移 / 需要特性) の画面は 1 項目「需要家管理」にまとめる
      {
        path: routePaths.customerList.path,
        nameKey: routePaths.customerList.name,
      },
      {
        path: routePaths.customerRate.path,
        nameKey: routePaths.customerRate.name,
      },
    ],
  },
  // 9. システム管理 (design の「管理・設定」 セクション先頭 = 直前に区切り線)
  //
  // **下層項目を持たない単一リンク** (`docs/specs/common/shared-components.md`
  // §1 表 8 行目。 2026-10-01 PdM 判断 #9228)。 押すと システム管理トップ
  // (`/admin/hub`) のカード一覧へ移り、 個別画面へはそのカードから入る。
  //
  // 旧実装は配下 18 画面を items に列挙していたが、 サイドメニューとハブが同じ画面を
  // 二重に並べる形になっていた (= ハブがメニューの写しになる)。 どちらを正にするかを
  // #9228 で PdM 判断し、 サイドメニュー側を畳むことにした。
  // 列挙をやめた 18 画面は `routes/routeIntegrity.test.ts` の MENU_EXEMPT_ROUTES に
  // 理由付きで登録してある (= メニューに載らない実装済み画面の記録先)。
  {
    icon: FaSlidersRegular,
    labelKey: 'common:nav.admin',
    prefix: '/admin',
    sectionDividerAbove: true,
    singleLink: true,
    items: [
      {
        path: routePaths.adminHub.path,
        nameKey: routePaths.adminHub.name,
      },
    ],
  },
]

/**
 * MenuGroup の items を i18n 解決済みの SubMenuItem[] に変換する。
 * TopMenu (flyout) と MainLayout (副 panel) が同一変換を行うため、 ここに集約する。
 */
export const toSubMenuItems = (group: MenuGroup, t: TFunction): SubMenuItem[] =>
  group.items.map((item) => {
    if (item.kind === 'section') {
      return { kind: 'section', label: t(item.labelKey) }
    }
    if (item.kind === 'divider') {
      return { kind: 'divider' }
    }
    return { path: item.path, label: t(item.nameKey) }
  })

/**
 * グループのドット = 描画される子の項目のドットをまとめたもの (AC-SC-041 の親グループ)。
 * ドットは `items` にある項目にだけ付くので、項目を隠す仕組みが入って `items` から
 * 外れれば、その項目のドットも親のドットに寄与しなくなる。
 */
export const resolveGroupDot = (
  group: MenuGroup,
  dots: ReadonlyMap<string, SideMenuDot>,
): SideMenuDot | undefined =>
  resolveGroupSideMenuDot(
    group.items.flatMap((item) =>
      item.kind === 'section' || item.kind === 'divider' ? [] : [item.path],
    ),
    dots,
  )

/**
 * URL が親の配下に入れ子になっていない詳細画面から、 current 表示にすべき
 * 親 menu item への写像。
 *
 * design 側 (`design/js/include-nav.js` の `DETAIL_TO_PARENT_HREF` と各 HTML の
 * `data-nav-active` 属性。例 `design/balance/bg-region.html` が
 * `data-nav-active="/balance/bg.html"`) は、 nav の current 判定を URL 構造に
 * 頼らず明示写像で解いている。 実装側にこの写像が無かったため、 URL 上は親の
 * 兄弟になる詳細画面で sub side menu の selected・group アイコンの点灯が
 * いずれも落ちていた (デザイナー指摘 2026-08-14「需要想定詳細を開いている際、
 * sub side menu の selected がない」)。
 *
 * ここに載せるのは「親が design 上で確定している」ものだけ。
 * `/bidding/supply-capacity-change` は design 側にも `data-nav-active` が無く
 * 親が未定義なので、 design 側の確定を待って別途追加する。
 *
 * [design 差異] design は query によって親を切り替える (関数値を許す形):
 * `design/demand/forecast-today.html:675-681` は `?caseId=` かつ `plan` 無し・
 * `readonly!=1` のとき親を `/demand/cases.html` (業務ワークフロー) に振り替える
 * (= ワークフロー詳細からの確定前作業は業務ワークフロー配下に吸収する)。
 * 本写像は pathname だけを見る静的写像なので、 その経路でも需要想定一覧が
 * current になる。 query を見るには `isMenuPathActive` の signature に search を
 * 通す必要があり、 3 経路 (sub menu / group アイコン / 副パネル auto-open) に
 * 波及するため本 PR では見送る (修正前は「どこも点灯しない」だったので回帰ではない)。
 */
const DETAIL_TO_PARENT_MENU_PATH: ReadonlyArray<readonly [string, string]> = [
  [routePaths.demandForecastDaily.path, routePaths.demandForecastList.path],
  [routePaths.demandForecastWeekly.path, routePaths.demandForecastList.path],
  // 需要家管理のタブ (契約者 / 契約推移 / 需要特性) の間も「需要家管理」を current にする
  [routePaths.customerContractors.path, routePaths.customerList.path],
  [routePaths.customerContract.path, routePaths.customerList.path],
  [routePaths.customerSchedule.path, routePaths.customerList.path],
  [routePaths.balanceDemandDetail.path, routePaths.balanceDemand.path],
  [routePaths.balanceGenerationDetail.path, routePaths.balanceGeneration.path],
  // 時間前入札 CSV インポートは spec intraday-csv-import.md §6 が URL を
  // `bidding/intraday-csv-import` (= 時間前入札画面の兄弟 URL) と定めているため、
  // スポット (`/bidding/spot/csv-import` = 配下パス) と違い前方一致では拾えない。
  [routePaths.biddingIntradayCsvImport.path, routePaths.biddingIntraday.path],
]

/**
 * 詳細画面の pathname から親 menu item のパスを引く。
 * 判定は react-router の `matchPath` で行う (末尾スラッシュ等の表記揺れを吸収する)。
 */
const resolveParentMenuPath = (pathname: string): string | undefined =>
  DETAIL_TO_PARENT_MENU_PATH.find(
    ([detailPath]) => matchPath(detailPath, pathname) !== null,
  )?.[1]

/**
 * pathname が menu item のパスに一致 (= 完全一致 or 配下の子パス or
 * [[DETAIL_TO_PARENT_MENU_PATH]] 経由の詳細画面) するか。
 * 詳細画面 (例: /workflow/cases/:caseId) を開いている間も親メニューを
 * current 表示にするため、 セグメント境界での前方一致で判定する
 * (/bidding/spot-price が /bidding/spot に一致しないよう `/` 区切りを要求)。
 *
 * この述語は sub side menu 項目の selected (`SubMenuItems`)・group アイコンの
 * active (`isPathInGroup` 経由)・副パネルの auto-open (`secondaryMenuAutoOpen`)
 * の 3 経路すべてが通るため、 ここを直すと 3 つ同時に整合する。
 */
export const isMenuPathActive = (pathname: string, path: string): boolean =>
  pathname === path ||
  pathname.startsWith(`${path}/`) ||
  resolveParentMenuPath(pathname) === path

const isLinkItem = (
  item: MenuGroupItem,
): item is Extract<MenuGroupItem, { path: string }> =>
  item.kind === undefined || item.kind === 'link'

/**
 * gate 付きの link 項目を、 その gate が `on` でなければ (= `pending` / `off`) 落とす。
 * 落とした結果 link が 1 件も無くなった group は group ごと落とす (副パネルが空の group を出さない)。
 * gate の無い項目と、 もともと link を持たない group は変えない。
 */
export const filterMenuGroups = (
  groups: readonly MenuGroup[],
  states: MenuGateStates,
): MenuGroup[] =>
  groups.flatMap((group) => {
    const items = group.items.filter(
      (item) =>
        !isLinkItem(item) ||
        item.gate === undefined ||
        states[item.gate] === 'on',
    )
    if (items.length === group.items.length) return [group]
    return items.some(isLinkItem) ? [{ ...group, items }] : []
  })

/**
 * group 内の link item と pathname が一致 (= 完全一致 or 親パス一致) するか。
 * 副パネルの自動表示 / メニューの active 判定で使う。
 */
export const isPathInGroup = (group: MenuGroup, pathname: string): boolean => {
  const matchesOwnItems = group.items.some(
    (item) => isLinkItem(item) && isMenuPathActive(pathname, item.path),
  )
  if (group.singleLink !== true) return matchesOwnItems

  // 単一リンクの group (= システム管理、 #9228) は配下の画面がメニューに載らないため、
  // items の一致だけでは配下にいる間 current が外れる。 prefix 配下も current にする。
  if (
    !matchesOwnItems &&
    pathname !== group.prefix &&
    !pathname.startsWith(`${group.prefix}/`)
  ) {
    return false
  }

  // **`/admin/` 配下には他 group が所有する画面が多数ある** (収益管理 `/admin/revenue-forecast`
  // `/admin/invoice`、 需給情報 `/admin/hjks` `/admin/area-balance` 等、 顧客管理
  // `/admin/customer-info/*`、 業務ワークフロー `/admin/workflow-templates`)。
  // prefix だけで判定すると、 それらの画面で**所有 group と システム管理 の 2 箇所が
  // 同時に current になる** (実測で 4 経路が該当)。 他 group の item に一致する pathname は除く。
  return !menuGroups.some(
    (other) =>
      other !== group &&
      other.items.some(
        (item) => isLinkItem(item) && isMenuPathActive(pathname, item.path),
      ),
  )
}

/**
 * 副パネル (と右端の chevron) を出すべき group か。
 *
 * 単一リンクの group (= システム管理、 #9228) には出さない。 DS は `secondaryMenu` の
 * 有無で chevron と hover popup の両方を決めるため、 ここが false の group には
 * `secondaryMenu` を渡さない (= 個人設定 / お知らせ と同じ扱い)。
 */
export const hasSubMenu = (group: MenuGroup): boolean =>
  group.singleLink !== true

/**
 * pathname を含むメニューグループを逆引きする。
 * URL prefix では表現できないグループ横断構造 (例: /admin/revenue-forecast が
 * 収益管理グループに属す) にも [[isPathInGroup]] 経由で対応する。
 */
export const findGroupByPathname = (pathname: string): MenuGroup | undefined =>
  menuGroups.find((group) => isPathInGroup(group, pathname))

/**
 * グループ見出しクリック時の遷移先 (= 表示中 items の先頭 link) を返す。
 * `docs/specs/common/shared-components.md` AC-SC-029 準拠。
 * 個別 landingPath を持たせず、 表示中 (= section/divider を除いた) items の
 * 先頭 link を常に自動採用する (= 下層メニューの並び替え/非表示制御との drift を防ぐ)。
 * 下層メニューが無い (= 全 item が非表示、 または items が空) 場合は undefined。
 */
export const getGroupLandingPath = (group: MenuGroup): string | undefined =>
  group.items.find(isLinkItem)?.path
